import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { CheckSquare, FileScan, ListChecks, Scale, Clock, TrendingUp, FileText, Gavel, ChevronDown, ShieldCheck, BarChart3, ArrowRight, Check } from 'lucide-react';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';
import { StepCard } from '../components/ui/StepCard';
import { PackageCard } from '../components/ui/PackageCard';
import { StatCard } from '../components/ui/StatCard';
import { FeatureCard } from '../components/ui/FeatureCard';

const FaqItem: React.FC<{ question: string; answer: string; index: number }> = ({ question, answer, index }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <RevealOnScroll delay={index * 50}>
            <div className={`border-b border-border-light/50 dark:border-border-dark/50 ${isOpen ? 'bg-white/5 dark:bg-white/5' : ''} transition-colors duration-300 rounded-lg`}>
                <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center py-5 px-4 text-left group">
                    <span className="font-semibold text-lg text-text-primary-light dark:text-text-primary-dark group-hover:text-primary-light dark:group-hover:text-primary-dark transition-colors">
                        {question}
                    </span>
                    <div className={`w-8 h-8 rounded-lg bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center transition-all duration-300 ${isOpen ? 'rotate-180 bg-primary-light/20 dark:bg-primary-dark/20' : ''}`}>
                        <ChevronDown className="w-5 h-5 text-primary-light dark:text-primary-dark" />
                    </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
                    <p className="pb-5 px-4 text-text-secondary-light dark:text-text-secondary-dark">{answer}</p>
                </div>
            </div>
        </RevealOnScroll>
    );
};

export const LegalCloudAiPage: React.FC = () => {
    usePageMetadata({
        title: "LegalCloud AI | Analiza Decyzji Podatkowych i Pism",
        description: "Zwiększ swoje szanse w sporach z urzędami. LegalCloud AI analizuje decyzje podatkowe i pisma, identyfikując błędy i podstawy do odwołania.",
    });

    const targetAudience = [
        'Otrzymali niekorzystną decyzję podatkową lub administracyjną',
        'Czują, że ich prawa zostały naruszone w postępowaniu',
        'Potrzebują profesjonalnej oceny swoich szans procesowych',
        'Szukają skutecznej reprezentacji przed sądami administracyjnymi',
        'Chcą odzyskać nienależnie pobrane podatki lub kary',
        'Cenią czas i nowoczesne narzędzia',
    ];

    const steps = [
        { number: 1, icon: <FileScan className="w-8 h-8" />, title: 'Analiza Dokumentów', description: 'AI kompleksowo przegląda akta sprawy, wykrywa niespójności, identyfikuje kluczowe daty i porównuje stan faktyczny z obowiązującymi przepisami.' },
        { number: 2, icon: <ListChecks className="w-8 h-8" />, title: 'Weryfikacja Wielopoziomowa', description: 'Każda decyzja jest sprawdzana pod kątem zgodności formalnej, proceduralnej, merytorycznej i kompletności dowodowej.' },
        { number: 3, icon: <Scale className="w-8 h-8" />, title: 'Identyfikacja Podstaw Odwoławczych', description: 'System automatycznie wykrywa naruszenia przepisów, braki w uzasadnieniu i niezgodności z orzecznictwem sądowym.' },
    ];

    const benefits = [
        { icon: <Clock className="w-8 h-8" />, title: 'Oszczędność Czasu', description: 'Analiza 500-stronicowych akt w 2 godziny zamiast 2 dni. Gotowe projekty pism procesowych.' },
        { icon: <TrendingUp className="w-8 h-8" />, title: 'Zwiększone Szanse Sukcesu', description: '87% skuteczności w sprawach z pełną analizą. Wykorzystanie najnowszego orzecznictwa NSA i TSUE.' },
        { icon: <FileText className="w-8 h-8" />, title: 'Kompleksowość Obsługi', description: 'Pełna dokumentacja procesowa, wsparcie na każdym etapie i bieżący monitoring terminów.' },
    ];

    const packages = [
        {
            name: 'Pakiet Podstawowy',
            price: 'od 5 000 zł',
            period: '',
            description: 'Analiza i Opinia',
            features: ['Raport Analizy Prawnej (30-40 stron)', 'Tabela Naruszeń z podstawami prawnymi', 'Harmonogram Działań i szacowane koszty'],
            isPopular: false,
        },
        {
            name: 'Pakiet Rozszerzony',
            price: 'od 10 000 zł',
            period: '',
            description: 'Pełna Reprezentacja',
            features: ['Wszystko z Pakietu Podstawowego', 'Profesjonalna Skarga do WSA', 'Wniosek o Wstrzymanie Wykonania decyzji', 'Przygotowanie wszystkich pism procesowych'],
            isPopular: true,
        },
        {
            name: 'Pakiet Premium',
            price: 'od 15 000 zł',
            period: '',
            description: 'Strategia Wielotorowa',
            features: ['Wszystko z Pakietu Rozszerzonego', 'Analiza alternatywnych ścieżek prawnych', 'Wsparcie w zakresie skargi konstytucyjnej', 'Analiza zgodności z prawem UE i TSUE'],
            isPopular: false,
        },
    ];

    const faqs = [
        { question: 'Czy system zastępuje prawnika?', answer: 'Nie - system jest zaawansowanym narzędziem, które wspiera i przyspiesza pracę doświadczonych prawników, zwiększając efektywność ich pracy i skuteczność reprezentacji.' },
        { question: 'Jaka jest skuteczność?', answer: 'W sprawach, w których podejmujemy się pełnej reprezentacji po analizie systemowej, 87% postępowań kończy się korzystnie dla klienta (uchylenie lub zmiana decyzji).' },
        { question: 'Ile to trwa?', answer: 'Wstępna analiza i raport strategiczny są gotowe w ciągu 24-48 godzin. Całe postępowanie sądowe przed WSA trwa zazwyczaj od 6 do 12 miesięcy.' },
        { question: 'Co jeśli przegramy?', answer: 'Nasza transparentna struktura opłat oznacza, że płacisz tylko za wykonaną pracę. W wybranych sprawach o wysokim prawdopodobieństwie sukcesu oferujemy również model wynagrodzenia oparty na rezultacie (success fee).' },
    ];

    const startSteps = [
        { number: 1, title: 'Bezpłatna Konsultacja', description: 'Wstępna ocena sprawy (15 minut), określenie szans i wycena usługi.' },
        { number: 2, title: 'Przekazanie Dokumentów', description: 'Bezpieczny transfer akt sprawy przez szyfrowany portal. Gwarancja poufności.' },
        { number: 3, title: 'Analiza i Strategia', description: 'Otrzymujesz pełny raport w 48h i omawiamy możliwe scenariusze działania.' },
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="purple" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                            <Gavel className="w-10 h-10 text-white" />
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            LegalCloud AI
                        </h1>
                        <p className="text-sm font-semibold text-primary-light dark:text-primary-dark uppercase tracking-wider mb-4">
                            System Profesjonalnej Analizy Decyzji Administracyjnych
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto mb-10">
                            Kompleksowe Wsparcie Prawne w Sporach z Organami Podatkowymi
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <a
                            href="https://bartoszgaca.pl/?utm_source=biznesbezklikania&utm_medium=hero_cta_legal&utm_campaign=bbk%E2%86%92bg"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group animate-glow"
                        >
                            Zleć wdrożenie
                            <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                        </a>
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* Target Audience Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 max-w-5xl relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Dla Kogo Jest Ten System?
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4">
                            Dla przedsiębiorców i podatników, którzy potrzebują skutecznego wsparcia w sporach z urzędami.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {targetAudience.map((item, index) => (
                            <RevealOnScroll key={index} delay={index * 50}>
                                <GlassCard variant="subtle" hover className="flex items-center gap-4 h-full">
                                    <div className="w-8 h-8 rounded-lg bg-primary-light/20 dark:bg-primary-dark/20 flex items-center justify-center flex-shrink-0">
                                        <Check className="w-5 h-5 text-primary-light dark:text-primary-dark" />
                                    </div>
                                    <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">{item}</p>
                                </GlassCard>
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-16 md:py-20 relative bg-content-light/50 dark:bg-content-dark/50">
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Jak Działa System?
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            Nasz 3-etapowy proces analizy maksymalizuje Twoje szanse na sukces.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {steps.map((step, index) => (
                            <RevealOnScroll key={step.number} delay={index * 150}>
                                <FeatureCard
                                    icon={step.icon}
                                    title={step.title}
                                    description={step.description}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="lines" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Korzyści dla Klientów
                        </h2>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {benefits.map((benefit, index) => (
                            <RevealOnScroll key={benefit.title} delay={index * 100}>
                                <FeatureCard
                                    icon={benefit.icon}
                                    title={benefit.title}
                                    description={benefit.description}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {/* Packages Section */}
            <section className="py-16 md:py-20 relative bg-content-light/50 dark:bg-content-dark/50">
                <AnimatedGrid variant="lines" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Co Otrzymujesz?
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            Wybierz pakiet dopasowany do Twoich potrzeb - od analizy po pełną reprezentację.
                        </p>
                    </RevealOnScroll>

                    <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 items-stretch max-w-6xl mx-auto">
                        {packages.map((pkg, index) => (
                            <RevealOnScroll key={pkg.name} delay={index * 100}>
                                <PackageCard
                                    name={pkg.name}
                                    price={pkg.price}
                                    period={pkg.period}
                                    description={pkg.description}
                                    features={pkg.features}
                                    isPopular={pkg.isPopular}
                                    ctaText="Wybieram pakiet"
                                    ctaHref="/kontakt"
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {/* ROI Section */}
            <section className="py-16 md:py-20 relative">
                <div className="container mx-auto px-4 max-w-5xl relative z-10">
                    <RevealOnScroll>
                        <GlassCard variant="strong" className="relative overflow-hidden">
                            <AnimatedGrid variant="dots" animated={false} />
                            <div className="relative z-10 grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
                                <div>
                                    <h2 className="text-3xl font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">
                                        Opłacalność Inwestycji
                                    </h2>
                                    <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6">
                                        Nasze statystyki pokazują, że inwestycja w profesjonalną analizę zwraca się wielokrotnie, chroniąc Twój biznes przed nieuzasadnionymi obciążeniami.
                                    </p>
                                    <div className="grid grid-cols-2 gap-6">
                                        <StatCard value="247 000 zł" label="Średnia kwota odzyskana" variant="compact" />
                                        <StatCard value="1:12" label="Średni zwrot z kosztu" variant="compact" />
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <GlassCard variant="subtle" className="p-0 overflow-hidden">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-white/10 dark:bg-white/5">
                                                <tr>
                                                    <th className="p-4 font-semibold text-text-primary-light dark:text-text-primary-dark">Inwestycja</th>
                                                    <th className="p-4 font-semibold text-text-primary-light dark:text-text-primary-dark">Potencjalny Zwrot</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="border-b border-border-light/30 dark:border-border-dark/30">
                                                    <td className="p-4 font-mono text-text-secondary-light dark:text-text-secondary-dark">5 000 - 15 000 zł</td>
                                                    <td className="p-4 font-mono text-primary-light dark:text-primary-dark font-semibold">50 000 - 500 000 zł</td>
                                                </tr>
                                                <tr>
                                                    <td className="p-4 text-text-secondary-light dark:text-text-secondary-dark">Analiza + Skarga</td>
                                                    <td className="p-4 text-text-secondary-light dark:text-text-secondary-dark">Uchylenie decyzji + odsetki</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </GlassCard>
                                </div>
                            </div>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            {/* Guarantees Section */}
            <section className="py-16 md:py-20 relative bg-content-light/50 dark:bg-content-dark/50">
                <div className="container mx-auto px-4 max-w-5xl relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Gwarancje i Bezpieczeństwo
                        </h2>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-2 gap-6">
                        <RevealOnScroll delay={100}>
                            <GlassCard variant="medium" className="h-full">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-light/20 to-secondary-light/20 dark:from-primary-dark/20 dark:to-secondary-dark/20 flex items-center justify-center">
                                        <ShieldCheck className="w-6 h-6 text-primary-light dark:text-primary-dark" />
                                    </div>
                                    <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">Gwarancja Jakości</h3>
                                </div>
                                <ul className="space-y-3 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {['Zgodność z Kodeksem Etyki Radcy Prawnego', 'Ubezpieczenie OC do 10 mln zł', 'Certyfikaty ISO w zakresie ochrony danych'].map((item, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <Check className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </GlassCard>
                        </RevealOnScroll>

                        <RevealOnScroll delay={200}>
                            <GlassCard variant="medium" className="h-full">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-light/20 to-secondary-light/20 dark:from-primary-dark/20 dark:to-secondary-dark/20 flex items-center justify-center">
                                        <BarChart3 className="w-6 h-6 text-primary-light dark:text-primary-dark" />
                                    </div>
                                    <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">Transparentność</h3>
                                </div>
                                <ul className="space-y-3 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {['Raport wstępny GRATIS przed podjęciem współpracy', 'Przejrzysta struktura opłat', 'Bieżące raportowanie postępów'].map((item, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <Check className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </GlassCard>
                        </RevealOnScroll>
                    </div>
                </div>
            </section>

            {/* How To Start Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Jak Rozpocząć?
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            Prosty proces w 3 krokach - od bezpłatnej konsultacji do gotowej strategii działania.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {startSteps.map((step, index) => (
                            <RevealOnScroll key={step.number} delay={index * 150}>
                                <StepCard
                                    number={step.number}
                                    title={step.title}
                                    description={step.description}
                                    animationDelay={index * 200}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 md:py-20 relative bg-content-light/50 dark:bg-content-dark/50">
                <div className="container mx-auto px-4 max-w-3xl relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Najczęstsze Pytania
                        </h2>
                    </RevealOnScroll>

                    <GlassCard variant="subtle" className="divide-y divide-border-light/30 dark:divide-border-dark/30">
                        {faqs.map((faq, index) => (
                            <FaqItem key={index} question={faq.question} answer={faq.answer} index={index} />
                        ))}
                    </GlassCard>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-16 md:py-20 relative">
                <div className="container mx-auto px-4 max-w-4xl relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Co Mówią Nasi Klienci?
                        </h2>
                    </RevealOnScroll>

                    <div className="space-y-6">
                        {[
                            { quote: 'System pozwolił nam odzyskać 1,2 mln zł nienależnie nałożonego VAT. Analiza wykazała 31 naruszeń proceduralnych, o których nasz poprzedni prawnik nie wspomniał.', author: 'Dyrektor Finansowy, branża IT' },
                            { quote: 'Dzięki kompleksowej analizie uniknęliśmy egzekucji komorniczej i wygraliśmy sprawę w WSA. ROI: 1:25', author: 'Właściciel firmy transportowej' },
                        ].map((testimonial, index) => (
                            <RevealOnScroll key={index} delay={index * 100}>
                                <GlassCard variant="medium" className="border-l-4 border-primary-light dark:border-primary-dark">
                                    <p className="text-lg italic text-text-secondary-light dark:text-text-secondary-dark mb-4">
                                        "{testimonial.quote}"
                                    </p>
                                    <footer className="font-semibold text-text-primary-light dark:text-text-primary-dark">
                                        — {testimonial.author}
                                    </footer>
                                </GlassCard>
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            <WhatNextSection diyLink="/rozwiazania/legal-cloud-ai" />
        </>
    );
};
